import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useState } from "react";
import styled from "styled-components";
import { IBenefitSelectionRule } from "types/Account/Account";

import { reorder } from "../BenefitSelection/mapper";
import { SectionTitle } from "../BenefitSelection/Surface";
import {
  BenefitSelectionRuleDialog,
  emptyRule,
  networkStatusOptions,
} from "./BenefitSelectionRuleDialog";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  /* Keyword lists are the point of the row, so let them wrap rather than truncate. */
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
`;

/** Chips, or an em dash — an empty list means something specific and should read as a value. */
function KeywordCell({
  values,
  emptyLabel,
}: {
  values?: string[];
  emptyLabel: string;
}) {
  if (!values?.length) {
    return <span className="text-color-secondary">{emptyLabel}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {values.map((value) => (
        <Tag key={value} value={value} className="surface-200 text-color" />
      ))}
    </div>
  );
}

/**
 * CLE-2683 — how this account reads each payer's benefit lines.
 *
 * Payers routinely return several priced lines for one service, separated only by wording: an ASC
 * copay of $250 marked "FLEX PROVIDER" beside $500 marked "OTHER PLAN PROVIDER". Which one is this
 * facility's is a fact about its contract, so it is entered here rather than guessed at.
 *
 * Listed as a table and edited in a dialog. The rules started life as stacked inline cards, which
 * stopped being readable at about three — accounts are heading for a rule per payer, so the shape
 * follows the contract adjustment rules instead.
 *
 * 🚨 CONTROLLED BY THE BENEFIT SELECTION CARD SINCE REVIEW ROUND 1. These rows used to live in the
 * account edit form's own field array, which meant "Update Hospital" wrote them AND the card's Save
 * wrote them, and the later of the two won whatever the earlier had done. The card is now the only
 * writer, so the rows are its state and this component just edits them.
 */
interface IBenefitSelectionRulesProps {
  value: IBenefitSelectionRule[];
  payerOptions: Array<{ label: string; value: number }>;
  loadingPayers: boolean;
  onChange: (rules: IBenefitSelectionRule[]) => void;
}

export function BenefitSelectionRules({
  value: rules,
  payerOptions,
  loadingPayers,
  onChange,
}: IBenefitSelectionRulesProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const payerLabel = (payerIds?: number[]): string[] | undefined => {
    if (!payerIds?.length) return undefined;
    return payerIds
      .map(
        (id) =>
          payerOptions.find((option) => option.value === id)?.label ?? `#${id}`
      )
      .sort();
  };

  const openAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const openEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (rule: IBenefitSelectionRule) => {
    if (editingIndex === null) {
      // CLE-2792 — A NEW FILTER GOES TO THE TOP. The list is checked top to bottom and the first
      // match wins, so a filter appended to the bottom is one that never fires for any payer an
      // earlier filter already covers — which reads as "my filter did nothing" rather than as a
      // position problem. Somebody who wants it lower drags it there.
      onChange([rule, ...rules]);
    } else {
      onChange(
        rules.map((current, index) => (index === editingIndex ? rule : current))
      );
    }
    setDialogOpen(false);
  };

  const move = (from: number, to: number) => onChange(reorder(rules, from, to));

  const confirmRemove = (index: number) =>
    confirmDialog({
      message: "Remove this payer filter? Removed on save.",
      header: "Remove payer filter",
      style: { width: "min(520px, 95vw)" },
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Remove",
      rejectLabel: "Cancel",
      accept: () => onChange(rules.filter((_, i) => i !== index)),
    });

  // Extracted as variables rather than inline arrow functions in the JSX, so
  // `react/no-unstable-nested-components` does not treat them as components redefined every render
  // (the same convention as the payer-rules tables).
  const orderCellBody = (_rule: IBenefitSelectionRule, { rowIndex }: any) => (
    <div className="flex align-items-center gap-1">
      <Button
        type="button"
        icon="pi pi-chevron-up"
        className="p-button-text p-button-sm"
        aria-label={`Move payer filter ${rowIndex + 1} up`}
        disabled={rowIndex === 0}
        data-testid={`benefitSelectionRules.${rowIndex}.up`}
        onClick={() => move(rowIndex, rowIndex - 1)}
      />
      <Button
        type="button"
        icon="pi pi-chevron-down"
        className="p-button-text p-button-sm"
        aria-label={`Move payer filter ${rowIndex + 1} down`}
        disabled={rowIndex === rules.length - 1}
        data-testid={`benefitSelectionRules.${rowIndex}.down`}
        onClick={() => move(rowIndex, rowIndex + 1)}
      />
    </div>
  );

  const payersCellBody = (_rule: IBenefitSelectionRule, { rowIndex }: any) => (
    <span data-testid={`benefitSelectionRules.${rowIndex}.payers`}>
      <KeywordCell
        values={payerLabel(rules[rowIndex]?.payerIds)}
        emptyLabel="All payers"
      />
    </span>
  );

  const plansCellBody = (_rule: IBenefitSelectionRule, { rowIndex }: any) => (
    <span data-testid={`benefitSelectionRules.${rowIndex}.plans`}>
      <KeywordCell
        values={rules[rowIndex]?.planKeywords}
        emptyLabel="All plans"
      />
    </span>
  );

  const matchCellBody = (_rule: IBenefitSelectionRule, { rowIndex }: any) => (
    <span data-testid={`benefitSelectionRules.${rowIndex}.match`}>
      <KeywordCell values={rules[rowIndex]?.matchKeywords} emptyLabel="—" />
    </span>
  );

  const excludeCellBody = (_rule: IBenefitSelectionRule, { rowIndex }: any) => (
    <span data-testid={`benefitSelectionRules.${rowIndex}.exclude`}>
      <KeywordCell values={rules[rowIndex]?.excludeKeywords} emptyLabel="—" />
    </span>
  );

  const networkCellBody = (_rule: IBenefitSelectionRule, { rowIndex }: any) => {
    const status = rules[rowIndex]?.networkStatus ?? "InNetwork";
    return (
      <span data-testid={`benefitSelectionRules.${rowIndex}.network`}>
        <Tag
          value={
            networkStatusOptions.find((option) => option.value === status)?.name
          }
          severity={status === "OutOfNetwork" ? "warning" : "success"}
        />
      </span>
    );
  };

  /**
   * CLE-2755 — the copay override, and the Service Types it covers.
   *
   * Shown as one cell rather than two because the amount is meaningless without its scope: "$100"
   * alone reads as a rule for every estimate this filter matches, which is exactly what it is NOT
   * when service types are listed. An override with no service types genuinely does cover them all,
   * and says so in words rather than by an empty column.
   */
  const copayOverrideCellBody = (
    _rule: IBenefitSelectionRule,
    { rowIndex }: any
  ) => {
    const rule = rules[rowIndex];
    // `== null` — a configured $0 is a real override and must not render as "—".
    if (rule?.flatCopayOverride == null) {
      return (
        <span data-testid={`benefitSelectionRules.${rowIndex}.copayOverride`}>
          —
        </span>
      );
    }

    const codes = rule.flatCopayOverrideServiceTypes ?? [];
    const names = codes.map(
      (code) =>
        mapPayerNameToCodeForDropdown.find((option) => option.value === code)
          ?.name ?? code
    );

    return (
      <span data-testid={`benefitSelectionRules.${rowIndex}.copayOverride`}>
        <Tag value={`$${rule.flatCopayOverride}`} severity="info" />
        <span className="block text-xs text-color-secondary mt-1">
          {names.length > 0 ? names.join(", ") : "Every service type"}
        </span>
      </span>
    );
  };

  const actionsCellBody = (_rule: IBenefitSelectionRule, { rowIndex }: any) => (
    <div className="flex gap-1">
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label="Edit rule"
        data-testid={`benefitSelectionRules.${rowIndex}.edit`}
        onClick={() => openEdit(rowIndex)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label="Remove rule"
        data-testid={`benefitSelectionRules.${rowIndex}.remove`}
        onClick={() => confirmRemove(rowIndex)}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-2">
      <SectionTitle>Payer filters</SectionTitle>

      <span className="text-sm text-color-secondary">
        Narrow what a payer&apos;s response says before anything is read.
      </span>

      <span className="text-sm text-color-secondary">
        Checked top to bottom; the first that matches the payer wins. Keywords
        are matched against the wording on each benefit line, ignoring case,
        anywhere in the text — so use the distinctive part (FLEX PROVIDER, not
        NETWORK). A filter with no payers covers every payer, and one with no
        plans covers every plan.
      </span>

      <StyledDataTable
        value={rules}
        reorderableRows
        onRowReorder={(event: any) => onChange(event.value)}
        emptyMessage="No payer filters — every response is read as the payer sent it."
        data-testid="benefitSelectionRulesTable"
        className="w-full"
      >
        {/* One row has no order to change, so the carets are not shown (Mike, 2026-09-14). */}
        {rules.length > 1 && (
          <Column header="" style={{ width: "9rem" }} body={orderCellBody} />
        )}
        <Column header="Payers" body={payersCellBody} />
        <Column header="Plans / products" body={plansCellBody} />
        <Column header="Prefer lines containing" body={matchCellBody} />
        <Column header="Never use" body={excludeCellBody} />
        <Column header="Network" body={networkCellBody} />
        <Column header="Copay override" body={copayOverrideCellBody} />
        <Column header="" style={{ width: "9rem" }} body={actionsCellBody} />
      </StyledDataTable>

      <div className="flex justify-content-center">
        <Button
          type="button"
          icon="pi pi-plus"
          label="Add payer filter"
          className="p-button-outlined p-button-sm"
          data-testid="addBenefitSelectionRule"
          onClick={openAdd}
        />
      </div>

      <BenefitSelectionRuleDialog
        visible={dialogOpen}
        isNew={editingIndex === null}
        value={
          editingIndex === null
            ? emptyRule()
            : rules[editingIndex] ?? emptyRule()
        }
        payerOptions={payerOptions}
        loadingPayers={loadingPayers}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </div>
  );
}
